import { sha256 } from 'js-sha256'

// Challenge-response nonce for CF-hosted funnel order submits (ENG-6754).
// GET /_cf/nonce returns executable JS (see UserPages::NoncesController /
// NonceIssuance) that calls window.__cfSdkHmacSha256 to compute the client
// signature, assigns window.__cfSdkNonce and dispatches `cf-sdk-nonce` —
// the same wire contract as the external-page SDK. The binding uuid is the
// cfhoy_visitor cookie, read server-side on both mint and verify, so this
// client never sends it.
//
// Fail-open by design: on error or timeout resolves null and the submit
// proceeds without nonce params — the server decides (report-only vs
// enforce, feature-gated per workspace).

export type CheckoutNonce = {
  challenge: string
  ts: number
  server_sig: string
  client_sig: string
}

const NONCE_PATH = '/_cf/nonce'
const NONCE_TIMEOUT_MS = 5000

export function fetchCheckoutNonce(): Promise<CheckoutNonce | null> {
  const win = window as any
  // Pure-JS HMAC, not Web Crypto: matches the SDK helper the server JS calls.
  if (!win.__cfSdkHmacSha256) {
    win.__cfSdkHmacSha256 = (key: string, data: string) => sha256.hmac(key, data)
  }

  return new Promise((resolve) => {
    win.__cfSdkNonce = null

    const script = document.createElement('script')
    let settled = false
    const finish = (value: CheckoutNonce | null) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      document.removeEventListener('cf-sdk-nonce', onNonce)
      script.remove()
      resolve(value)
    }
    const onNonce = () => finish(win.__cfSdkNonce || null)
    // Nonces are single-use with a 1-minute TTL, so a hung fetch is worth
    // less than a submit that lets the server decide.
    const timer = setTimeout(() => finish(null), NONCE_TIMEOUT_MS)

    document.addEventListener('cf-sdk-nonce', onNonce)
    script.src = NONCE_PATH
    script.async = true
    script.onerror = () => finish(null)
    document.head.appendChild(script)
  })
}

// Wire params verified by OrderProcessingConcern#valid_checkout_nonce?.
export function checkoutNonceParams(nonce: CheckoutNonce | null): Record<string, string> {
  if (!nonce) return {}
  return {
    sdk_challenge: nonce.challenge,
    sdk_ts: String(nonce.ts),
    sdk_server_sig: nonce.server_sig,
    sdk_client_sig: nonce.client_sig,
  }
}
